import { ChevronRight } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { cn, Skeleton } from "@nexus/ui";
import { SortHeader } from "@/features/identity-hub/components/dashboard/shared/table/SortHeader";
import type { SortState } from "@/features/identity-hub/lib/sort";
import { sortTableRows, toggleUsageSort, type SortValue } from "@/features/admin/lib/table-sort";
import {
  ADMIN_CELL_CLASS,
  ADMIN_NUMERIC_CELL_CLASS,
  ADMIN_STICKY_HEAD_CLASS,
  ADMIN_TABLE_HEADER_CLASS,
} from "./admin-layout-tokens";

export interface AdminColumn<Row, Key extends string> {
  /** Doubles as the sort key when `isSortable`. */
  id: Key;
  header: string;
  /** Right-aligned and tabular. Every measure is numeric; every label is not. */
  isNumeric?: boolean;
  /**
   * Takes the spare width so the numeric columns collapse onto their contents.
   * Exactly one column per table, and it is the label column — left to itself
   * the browser deals width out evenly and strands each number at the far edge
   * of its own wide cell.
   */
  isFill?: boolean;
  isSortable?: boolean;
  cell: (row: Row) => ReactNode;
  /** The precise value the cell abbreviates, or why it reads as it does. */
  cellTitle?: (row: Row) => string | undefined;
  cellClassName?: string;
}

/**
 * Clicking a row scopes the page to what it names. Distinct from opening one:
 * a selection narrows what is already on screen, and clicking the selected row
 * again is the way back out.
 */
export interface AdminRowSelect<Row> {
  onSelect: (row: Row) => void;
  isSelected: (row: Row) => boolean;
  /**
   * What activating this row does, read aloud. `null` leaves the row inert —
   * some rows name nothing the page can scope to.
   */
  getLabel: (row: Row, isSelected: boolean) => string | null;
}

/** Clicking a row opens the one thing it describes. Adds a chevron column. */
export interface AdminRowOpen<Row> {
  onOpen: (row: Row) => void;
  getLabel: (row: Row) => string;
}

interface AdminDataTableProps<Row, Key extends string> {
  /** Names the table for a screen reader. Never rendered. */
  caption: string;
  columns: AdminColumn<Row, Key>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  isLoading: boolean;
  /** Rows belong to the previous filter while the next one is in flight. */
  isStale?: boolean;
  /** Shown when the window itself held nothing. */
  emptyMessage: string;
  /**
   * Shown instead when the caller's own controls emptied the table. An empty
   * window and a filter that matched nothing are different problems, and only
   * one of them is the reader's to fix.
   */
  noMatchMessage?: string;
  /** True when a control of the caller's could be what emptied the table. */
  isFiltered?: boolean;
  /**
   * Sorts in memory. Omit for rows the server already ranked, which leaves the
   * order exactly as it arrived.
   */
  sortValue?: (row: Row, key: Key) => SortValue;
  /** Where an uncontrolled table opens. Required if any column is sortable. */
  defaultSort?: SortState<Key>;
  /** Pass to control the sort from outside — a server-ranked table does. */
  sort?: SortState<Key>;
  onSortChange?: (sort: SortState<Key>) => void;
  rowSelect?: AdminRowSelect<Row>;
  rowOpen?: AdminRowOpen<Row>;
}

const SKELETON_ROWS = 3;

/** Matches no column id, so every header renders unsorted. */
const UNSORTED = { key: "", dir: "desc" } as const;

/**
 * Every table in the admin panel.
 *
 * It owns the parts that were being re-decided per table and getting different
 * answers: header casing and weight, numeric alignment, what a sorted column
 * announces, what an empty result says, and what clicking a row means. A page
 * supplies columns and cells; it does not supply layout.
 *
 * Sorting is uncontrolled unless `sort` is passed. In-memory sorting is right
 * here because these tables render a complete aggregate — every model, every
 * tenant — so there is no page to re-fetch and the sort stays instant while a
 * refetch is in flight.
 */
export function AdminDataTable<Row, Key extends string>({
  caption,
  columns,
  rows,
  getRowId,
  isLoading,
  isStale = false,
  emptyMessage,
  noMatchMessage,
  isFiltered = false,
  sortValue,
  defaultSort,
  sort,
  onSortChange,
  rowSelect,
  rowOpen,
}: AdminDataTableProps<Row, Key>) {
  const [ownSort, setOwnSort] = useState<SortState<Key> | null>(defaultSort ?? null);
  const activeSort = sort ?? ownSort;

  const handleSort = (key: Key) => {
    // Which columns open ascending is a property of the column, not of the
    // page: a text column reads A–Z, and a magnitude column opens on its
    // biggest row because that is the question behind the click.
    const textKeys = new Set(
      columns.filter((column) => !column.isNumeric).map((column) => column.id),
    );
    const next = toggleUsageSort(activeSort ?? (UNSORTED as SortState<Key>), key, textKeys);

    if (sort == null) setOwnSort(next);
    onSortChange?.(next);
  };

  const sortedRows = useMemo(() => {
    if (!sortValue || !activeSort) return rows;
    return sortTableRows(rows, activeSort, sortValue);
  }, [rows, activeSort, sortValue]);

  const columnCount = columns.length + (rowOpen ? 1 : 0);

  return (
    <table className="w-full border-collapse">
      <caption className="sr-only">{caption}</caption>
      <thead className={ADMIN_STICKY_HEAD_CLASS}>
        {/* `group` arms the hover-preview chevron on the inactive columns. */}
        <tr className="group">
          {columns.map((column) =>
            column.isSortable ? (
              <SortHeader
                key={column.id}
                id={column.id}
                label={column.header}
                sort={activeSort ?? (UNSORTED as SortState<Key>)}
                onSort={handleSort}
                className={cn(
                  ADMIN_TABLE_HEADER_CLASS,
                  column.isFill && "w-full",
                  column.isNumeric && "text-right",
                )}
              />
            ) : (
              <th
                key={column.id}
                scope="col"
                className={cn(
                  ADMIN_TABLE_HEADER_CLASS,
                  column.isFill && "w-full",
                  column.isNumeric && "text-right",
                )}
              >
                {column.header}
              </th>
            ),
          )}
          {rowOpen ? <th className={ADMIN_TABLE_HEADER_CLASS} /> : null}
        </tr>
      </thead>

      {/* The header keeps its shape so the table does not resize while the
          numbers underneath are known to belong to the old filter. */}
      <tbody
        className={cn("transition-opacity duration-200", isStale && "opacity-40")}
        aria-busy={isStale || undefined}
      >
        {isLoading && rows.length === 0 ? (
          Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <tr key={index} className="border-b border-border last:border-0">
              <td className={ADMIN_CELL_CLASS} colSpan={columnCount}>
                <Skeleton className="h-4 w-full rounded-sm bg-foreground/8" />
              </td>
            </tr>
          ))
        ) : sortedRows.length === 0 ? (
          <tr>
            <td
              className={cn(ADMIN_CELL_CLASS, "py-6 text-center text-muted-foreground")}
              colSpan={columnCount}
            >
              {isFiltered && noMatchMessage ? noMatchMessage : emptyMessage}
            </td>
          </tr>
        ) : (
          sortedRows.map((row) => {
            const isSelected = rowSelect?.isSelected(row) ?? false;
            const selectLabel = rowSelect?.getLabel(row, isSelected) ?? null;
            const canSelect = rowSelect != null && selectLabel != null;

            return (
              <tr
                key={getRowId(row)}
                onClick={
                  rowOpen
                    ? () => rowOpen.onOpen(row)
                    : canSelect
                      ? () => rowSelect.onSelect(row)
                      : undefined
                }
                className={cn(
                  "border-b border-border last:border-0",
                  (canSelect || rowOpen != null) &&
                    "cursor-pointer transition-colors hover:bg-muted/40",
                  isSelected && "bg-primary/5 hover:bg-primary/5",
                )}
              >
                {columns.map((column, index) => {
                  const content = column.cell(row);
                  return (
                    <td
                      key={column.id}
                      className={cn(
                        ADMIN_CELL_CLASS,
                        column.isNumeric && ADMIN_NUMERIC_CELL_CLASS,
                        column.cellClassName,
                      )}
                      title={column.cellTitle?.(row)}
                    >
                      {/* The focus target lives on the first cell rather than on
                          the row: a focusable `tr` is reachable but announces
                          nothing about what activating it does, and a button
                          here both names the action and lets the click bubble
                          to the row's single handler. */}
                      {index === 0 && canSelect ? (
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          aria-label={selectLabel ?? undefined}
                          className="flex items-center gap-2 rounded-sm text-left outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          {content}
                        </button>
                      ) : (
                        content
                      )}
                    </td>
                  );
                })}

                {rowOpen ? (
                  <td className={cn(ADMIN_CELL_CLASS, "w-8 text-right")}>
                    <button
                      type="button"
                      aria-label={rowOpen.getLabel(row)}
                      className="rounded-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <ChevronRight className="size-4" aria-hidden />
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}
